import type { LandingCopy, LandingLocale } from './types';

const EN_COPY: LandingCopy = {
  locale: 'en',
  jobsHref: '/en/jobs',
  resourcesHref: '/en/resources',
  nav: {
    features: 'Features',
    howItWorks: 'How it works',
    resources: 'Resources',
    faq: 'FAQ',
    searchCta: 'Search Jobs →',
    signInText: 'Sign in',
    langHrefs: { en: '/en', fi: '/fi', sv: '/sv' },
  },
  userMenu: {
    menu: 'Menu',
    close: 'Close',
    findJobs: 'Find Jobs',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeAuto: 'Auto',
    activity: 'Your Activity',
    savedJobs: 'Saved Jobs',
    viewedJobs: 'Viewed Jobs',
    appliedJobs: 'Applied Jobs',
    savedSearches: 'Saved Searches',
    resources: 'Resources & Blog',
    locations: 'Locations Hub',
    profile: 'My Profile',
    session: 'Session',
    signOut: 'Sign out',
    signIn: 'Sign in',
    getStarted: 'Get started',
    placeholderName: 'Your name',
    placeholderHint: 'Sign in to save jobs, track applications, and keep your CV on JobCrawls.',
    login: 'Login',
    installApp: 'Install App',
  },
  hero: {
    eyebrow: 'For serious job seekers in Finland',
    h1Before: 'Finnish boards + global roles, ',
    h1Em: 'search and track together',
    lead: 'Search thousands of company career pages, Työmarkkinatori and international listings in one place. Track every application as you go.',
    sub: 'JobCrawls aggregates 17,500+ roles, including remote jobs available to Finnish residents that no other site shows. Available in Finnish, Swedish, and English.',
    subRich: [
      { type: 'text', text: 'JobCrawls aggregates ' },
      { type: 'stat', id: 'activeJobs', value: '17,500+' },
      { type: 'text', text: ' open jobs from thousands of ' },
      {
        type: 'link',
        label: 'company career pages and Työmarkkinatori',
        href: '/en/resources/jobs-in-finland/best-job-websites-finland',
      },
      { type: 'text', text: ' plus international remote platforms, with full ' },
      {
        type: 'link',
        label: 'Helsinki market data',
        href: '/en/resources/helsinki-job-market-2026',
      },
      { type: 'text', text: ' and ' },
      {
        type: 'link',
        label: '2026 salary transparency law',
        href: '/en/resources/finland-salary-transparency-law-2026',
      },
      { type: 'text', text: ' coverage. Browse our ' },
      { type: 'link', label: 'city-by-city hubs', href: '/en/locations' },
      { type: 'text', text: ' or dive into our ' },
      { type: 'link', label: 'Finland job-market guides', href: '/en/resources' },
      { type: 'text', text: '.' },
    ],
    searchKeywordPlaceholder: 'Job title, skill, or keyword…',
    searchLocationPlaceholder: 'City or remote',
    searchBtn: 'Search jobs',
    tags: [
      { label: 'Remote', href: '/en/jobs?remoteness=remote' },
      { label: 'English-language', href: '/en/jobs?requiredLanguages=english' },
      { label: 'Software Engineering', href: '/en/jobs?field=software+engineering' },
      { label: 'Helsinki', href: '/en/jobs/location/Helsinki_Finland' },
      { label: 'Full-time', href: '/en/jobs?jobType=full-time' },
      { label: 'Senior roles', href: '/en/jobs?normalisedRoleLevel=senior' },
      { label: 'Flexible hours', href: '/en/jobs?benefits=flexible+working+hours' },
      { label: 'Job Boards', href: '/en/resources/jobs-in-finland/best-job-websites-finland' },
    ],
    trackCta: 'Track your applications, free →',
  },
  seoPitch: {
    regionAriaLabel: 'How JobCrawls helps serious job seekers in Finland',
    moreLabel: 'More about JobCrawls: who it’s for and where the data comes from',
    points: [
      {
        title: 'Every open role in one search',
        desc: 'Thousands of company career pages, Työmarkkinatori and international sources all in one place: the openings you would otherwise chase across dozens of sites, plus global listings. No registration required.',
      },
      {
        title: 'Track every application',
        desc: 'Log where you applied, what stage you are at, and when to follow up. One job application tracker so you can track job applications without another spreadsheet tab.',
      },
      {
        title: 'International jobs alongside Finnish ones',
        desc: 'Not limiting your search to Finland? Remote European roles sit alongside Finnish listings so you see the full picture.',
      },
    ],
    audience:
      'JobCrawls is built for serious job seekers who treat job search Finland like a project. If you visit all Finnish job boards daily, track applications in a spreadsheet, and want international options alongside Finnish roles, this is for you. One job search tool Finland residents can use end-to-end. Not for recruiters. Not for employers.',
    sources:
      'Job aggregator Finland: we crawl company career pages directly and aggregate Työmarkkinatori, Indeed and international sources. Updated daily.',
    trustRich: [
      { type: 'text', text: 'JobCrawls is operated by TechTactics Oy. Read our ' },
      { type: 'link', label: 'about page', href: '/en/about' },
      { type: 'text', text: ', ' },
      { type: 'link', label: 'editorial standards', href: '/en/editorial-standards' },
      { type: 'text', text: ', or ' },
      { type: 'link', label: 'contact us', href: '/en/contact' },
      { type: 'text', text: ' with questions about listings or data.' },
    ],
  },
  stats: [
    { num: '17,500+', label: 'Live job listings', id: 'activeJobs' },
    { num: '3', label: 'Languages supported' },
    { num: '1,000s', label: 'Companies crawled' },
    { num: 'Free', label: 'Always, no paywalls' },
  ],
  introCards: [
    { iconType: 'search', title: 'Jobs others miss', desc: 'We crawl hundreds of sources (company career pages, agencies, remote boards), not just paid listings.' },
    { iconType: 'filter', title: 'Deep search & filters', desc: 'Filter by salary, language, benefits, seniority, and 50+ more attributes extracted from every listing.' },
    { iconType: 'check', title: 'Track your whole journey', desc: 'Save jobs, set search alerts, and track applications from first look to offer.' },
  ],
  features: [
    {
      badge: 'Search & Discovery',
      title: 'The most powerful job search in Finland',
      desc: 'Every listing includes structured data: salary ranges, required languages, benefits, work arrangements, and more. Search the way you actually think.',
      points: [
        'Filter by 50+ extracted attributes',
        'Sort by salary, relevance, or freshness',
        'Remote-global jobs available to Finnish residents',
        'Save searches and get email alerts',
      ],
      visualType: 'job-list',
      mockupUrl: 'jobcrawls.com/en/jobs',
      reversed: false,
    },
    {
      badge: 'Job Journey',
      title: 'Manage your entire job search in one place',
      desc: 'Track every job from first click to offer. Your funnel, your timeline, your notes. All synced across devices when you sign in.',
      points: [
        'Kanban-style application tracker',
        'Sankey funnel view of your pipeline',
        'Viewed, saved, and applied job history',
        'Syncs across all your devices',
      ],
      visualType: 'kanban',
      mockupUrl: 'Your Activity',
      reversed: true,
      ctaText: 'View your application funnel →',
      ctaHref: '/en/tools/job-flow-sankey',
    },
    {
      badge: 'Company Intelligence',
      title: 'Know the company before you apply',
      desc: 'Every employer has a profile built from public data: hiring trends, employer scores, financial figures, and work arrangement breakdowns.',
      points: [
        'JobCrawls Employer Score (pay, flexibility, stability, growth)',
        'Revenue and profitability from Finnish business registry',
        'Hiring trend signals: stable, growing, declining',
        'Best Employers ranking for Finland',
      ],
      visualType: 'employer-score',
      mockupUrl: 'jobcrawls.com/en/company/…',
      reversed: false,
      ctaText: 'Browse the companies hub →',
      ctaHref: '/en/best-companies',
    },
  ],
  langStrip: {
    label: 'Available in',
    pills: [
      { flag: '🇫🇮', name: 'Suomi', countValue: '17,500+', countUnit: 'työpaikkaa', href: '/fi' },
      { flag: '🇸🇪', name: 'Svenska', countValue: '17,500+', countUnit: 'jobb', href: '/sv' },
      { flag: '🇬🇧', name: 'English', countValue: '17,500+', countUnit: 'jobs', href: '/en' },
    ],
    ctaText: 'All listings machine-translated across all three languages →',
    ctaHref: '/en/jobs',
  },
  howItWorks: {
    sectionLabel: 'How it works',
    title: 'From search to offer in one place',
    steps: [
      { num: '1', title: 'Search everything', desc: 'Type a role, skill, or city. We search hundreds of sources, including jobs no paid board would list.' },
      { num: '2', title: 'Filter precisely', desc: 'Narrow by salary, language, benefits, seniority, and work arrangement from 50+ extracted attributes.' },
      { num: '3', title: 'Save & compare', desc: 'Bookmark roles, compare company profiles, and set up alerts for new matches.' },
      { num: '4', title: 'Track your progress', desc: 'Log applications, track interview stages, and visualise your pipeline, synced to your account.' },
    ],
  },
  resources: {
    sectionLabel: 'Resources & Insights',
    title: 'Finland job market guides',
    introRich: [
      { type: 'text', text: 'Data-driven guides on salaries, hiring trends, and job search in Finland, from ' },
      {
        type: 'link',
        label: 'comparing Finnish job sites',
        href: '/en/resources/jobs-in-finland/best-job-websites-finland',
      },
      { type: 'text', text: ' to ' },
      {
        type: 'link',
        label: 'Helsinki market data',
        href: '/en/resources/helsinki-job-market-2026',
      },
      { type: 'text', text: ' and the ' },
      {
        type: 'link',
        label: '2026 pay transparency law',
        href: '/en/resources/finland-salary-transparency-law-2026',
      },
      { type: 'text', text: '. See ' },
      { type: 'link', label: 'all guides', href: '/en/resources' },
      { type: 'text', text: ' or start with a featured article below.' },
    ],
    seeAllText: 'See all articles →',
    seeAllHref: '/en/resources',
    readMoreText: 'Read more →',
    cards: [
      {
        featured: true,
        href: '/en/resources/jobs-in-finland/best-job-websites-finland',
        imgSrc: 'https://www.jobcrawls.com/resources/job-websites-finland.webp',
        imgAlt: 'Best job websites Finland',
        date: '28 November 2025',
        title: 'Best Job Websites in Finland',
        desc: 'The best job search websites in Finland for finding your next role.',
      },
      {
        href: '/en/resources/helsinki-job-market-2026',
        imgSrc: 'https://www.jobcrawls.com/resources/helsinki-job-market.webp',
        imgAlt: 'Helsinki job market 2026',
        date: '27 March 2026',
        title: 'Helsinki Job Market 2026: Salaries, Remote & Skills',
        desc: "Salary trends, remote and hybrid roles, and the most in-demand skills in Finland's capital.",
      },
      {
        href: '/en/resources/finland-salary-transparency-law-2026',
        imgSrc: 'https://www.jobcrawls.com/resources/salary-transparency-in-finland.webp',
        imgAlt: 'Finland pay transparency law 2026',
        date: '31 March 2026',
        title: "Finland's Pay Transparency Law 2026",
        desc: 'From 18 May 2026, employers must share salary ranges before negotiations.',
      },
      {
        href: '/en/resources/jobs-in-finland/technology-salary-guide-finland-2026-04',
        imgSrc: 'https://www.jobcrawls.com/resources/tech-salaries-in-finland.webp',
        imgAlt: 'Technology salary guide Finland April 2026',
        date: '2 April 2026',
        title: 'Technology Salary Guide: Finland, April 2026',
        desc: 'Monthly salaries by seniority, top roles from trades to software engineers, and onsite vs hybrid vs remote. Based on 1,831 listings.',
      },
      {
        href: '/en/resources/jobs-in-finland/finland-hidden-job-market-networking-2026-03',
        imgSrc: 'https://www.jobcrawls.com/resources/who-you-know-matters-in-finland.webp',
        imgAlt: 'Hidden job market Finland',
        date: '29 March 2026',
        title: "Finland's Hidden Job Market & Networking",
        desc: '21% of Finnish jobs never reach Työmarkkinatori. Why networking matters and what to do about it.',
      },
      {
        href: '/en/resources/remote-work-in-finland',
        imgSrc: 'https://www.jobcrawls.com/resources/remote-work-in-finland.webp',
        imgAlt: 'Remote work Finland',
        date: '27 March 2026',
        title: 'Remote Work in Finland (2026)',
        desc: 'How common is fully remote work? Field and seniority splits from 16,700+ Finnish listings.',
      },
    ],
  },
  locations: {
    sectionLabel: 'Job Locations',
    title: 'Every city in Finland, in one search',
    desc: 'Live job counts for every Finnish city, region, and remote category, updated daily. Click any location for salary data, top employers, and in-demand skills.',
    cards: [
      { name: 'Helsinki', count: '5,200+', href: '/en/jobs/location/Helsinki_Finland', emoji: '🏙️', weight: 100 },
      { name: 'Remote', count: '3,600+', href: '/en/jobs?remoteness=remote', emoji: '🌍', weight: 69 },
      { name: 'Tampere', count: '1,500+', href: '/en/jobs/location/Tampere_Finland', emoji: '🏭', weight: 29 },
      { name: 'Espoo', count: '900+', href: '/en/jobs/location/Espoo_Finland', emoji: '🏢', weight: 17 },
      { name: 'Turku', count: '850+', href: '/en/jobs/location/Turku_Finland', emoji: '⚓', weight: 16 },
      { name: 'Oulu', count: '700+', href: '/en/jobs/location/Oulu_Finland', emoji: '❄️', weight: 13 },
      { name: 'Vantaa', count: '620+', href: '/en/jobs/location/Vantaa_Finland', emoji: '✈️', weight: 12 },
      { name: 'Jyväskylä', count: '400+', href: '/en/jobs/location/Jyväskylä_Finland', emoji: '🌲', weight: 8 },
      { name: 'Lahti', count: '360+', href: '/en/jobs/location/Lahti_Finland', emoji: '⛷️', weight: 7 },
      { name: 'Kuopio', count: '280+', href: '/en/jobs/location/Kuopio_Finland', emoji: '🎣', weight: 6 },
      { name: 'Pori', count: '220+', href: '/en/jobs/location/Pori_Finland', emoji: '🎷', weight: 5 },
      { name: 'Vaasa', count: '200+', href: '/en/jobs/location/Vaasa_Finland', emoji: '⚡', weight: 4 },
      { name: 'Lappeenranta', count: '170+', href: '/en/jobs/location/Lappeenranta_Finland', emoji: '🚤', weight: 3 },
      { name: 'Kotka', count: '140+', href: '/en/jobs/location/Kotka_Finland', emoji: '🚢', weight: 2 },
    ],
    viewAllText: 'View all locations →',
    viewAllHref: '/en/locations',
  },
  freeBanner: {
    title: 'Completely free.\nNo catches.',
    body: 'No job seeker should pay to find a job. JobCrawls is free: no premium tier, no locked filters, no hidden paywalls.',
    primaryCta: { text: 'Browse all jobs →', href: '/en/jobs' },
    secondaryCta: { text: 'Best employers in Finland', href: '/en/best-companies' },
  },
  faq: {
    sectionLabel: 'FAQ',
    title: 'Common questions',
    items: [
      {
        q: 'What makes JobCrawls different from Duunitori or Jobly?',
        a: "Duunitori and Jobly only show listings that employers pay to post there. JobCrawls crawls hundreds of sources (company career pages, niche boards, international remote platforms), so you see thousands of jobs that never appear on those sites. We also extract far richer structured data per listing, enabling filtering that Duunitori and Jobly simply don't offer.",
      },
      {
        q: 'Why are there jobs on JobCrawls that I can\'t find on Duunitori?',
        a: "Because those companies never submitted their vacancies to Duunitori. Many employers post only on their own careers page or on specialised boards. We crawl all of them directly, so we typically index thousands of roles that Duunitori never shows.",
      },
      {
        q: 'How does the filtering compare to Duunitori or Jobly?',
        a: 'Duunitori and Jobly offer basic keyword and location search. JobCrawls extracts 50+ structured attributes from every listing (salary ranges, required languages, benefits, work arrangements, seniority level, skills, and more), so you can filter the way you actually think about a job.',
      },
      {
        q: 'I\'m an expat or international jobseeker in Finland. Is JobCrawls better for me?',
        a: 'Yes. The full site runs in English (as well as Finnish and Swedish), and we specifically surface English-language roles and global remote jobs accessible from Finland, categories that Duunitori and Jobly largely ignore.',
      },
      {
        q: 'Are there really remote jobs for people based in Finland?',
        a: 'Yes, thousands of them. Many global companies hire fully remote workers anywhere in Europe or worldwide. JobCrawls surfaces these roles and makes them searchable alongside local Finnish listings. Duunitori and Jobly rarely index them.',
      },
      {
        q: 'Do Duunitori or Jobly have application tracking?',
        a: "Neither offers built-in application management. JobCrawls gives you a Kanban board to track every role from first view to offer, plus a Sankey funnel that shows your whole pipeline in one view.",
      },
      {
        q: 'Is JobCrawls really free?',
        a: "Completely free for job seekers: no tiers, no locked filters, no credits. Because no employer pays to be listed, rankings are by relevance and recency, not by who has the biggest recruitment budget.",
      },
      {
        q: 'How fresh are the listings compared to Duunitori?',
        a: 'We crawl sources continuously, with most listings updated within 24 hours. You can also filter by recency (past 6 hours, 24 hours, 3 days, or 1 week) to focus only on the freshest openings.',
      },
    ],
  },
  footer: {
    tagline:
      'One place to search across Finnish boards and track applications. UI in Finnish, Swedish, and English. Always free for job seekers.',
    cols: [
      {
        title: 'Search',
        links: [
          { label: 'Open jobs in Finland', href: '/en/jobs' },
          { label: 'Jobs in Helsinki', href: '/en/jobs/location/Helsinki_Finland' },
          { label: 'Jobs in Tampere', href: '/en/jobs/location/Tampere_Finland' },
          { label: 'Jobs in Espoo', href: '/en/jobs/location/Espoo_Finland' },
          { label: 'Jobs in Turku', href: '/en/jobs/location/Turku_Finland' },
          { label: 'Jobs in Oulu', href: '/en/jobs/location/Oulu_Finland' },
          { label: 'All locations →', href: '/en/locations' },
        ],
      },
      {
        title: 'Tools',
        links: [
          { label: 'How to find a job in Finland', href: '/en/job-search/how-to-find-a-job-in-finland' },
          { label: 'Job application tracker', href: '/en/job-search/job-application-tracker' },
          { label: 'Careers for juniors', href: '/en/juniors' },
          { label: 'Employer Score methodology', href: '/en/about-employer-score' },
          { label: 'Application tracker tool', href: '/en/tools/job-flow-sankey' },
          { label: 'Best employers', href: '/en/best-companies' },
          { label: 'Salary comparison', href: '/en/resources/jobs-in-finland/highest-salaries-finland' },
          { label: 'Sign in', href: '/auth/login' },
        ],
      },
      {
        title: 'Company',
        links: [
          { label: 'About JobCrawls', href: '/en/about' },
          { label: 'Blog & resources', href: '/en/resources' },
          { label: 'Contact', href: '/en/contact' },
          { label: 'Editorial standards', href: '/en/editorial-standards' },
          { label: 'Media partnerships', href: '/en/partnerships' },
          { label: 'Privacy Policy', href: '/en/privacy' },
          { label: 'Terms of Service', href: '/en/terms' },
        ],
      },
    ],
    copy: '© 2026 TechTactics Oy. All rights reserved.',
  },
};

const FI_COPY: LandingCopy = {
  locale: 'fi',
  jobsHref: '/fi/tyopaikat',
  resourcesHref: '/fi/tyoelama',
  nav: {
    features: 'Ominaisuudet',
    howItWorks: 'Miten toimii',
    resources: 'Työelämä',
    faq: 'UKK',
    searchCta: 'Hae töitä →',
    signInText: 'Kirjaudu',
    langHrefs: { en: '/en', fi: '/fi', sv: '/sv' },
  },
  userMenu: {
    menu: 'Valikko',
    close: 'Sulje',
    findJobs: 'Etsi töitä',
    language: 'Kieli',
    theme: 'Teema',
    themeLight: 'Vaalea',
    themeDark: 'Tumma',
    themeAuto: 'Auto',
    activity: 'Toimintasi',
    savedJobs: 'Tallennetut työpaikat',
    viewedJobs: 'Katsotut työpaikat',
    appliedJobs: 'Haetut työpaikat',
    savedSearches: 'Tallennetut haut',
    resources: 'Resurssit & Blogi',
    locations: 'Sijaintipalvelu',
    profile: 'Profiilini',
    session: 'Istunto',
    signOut: 'Kirjaudu ulos',
    signIn: 'Kirjaudu sisään',
    getStarted: 'Aloita',
    placeholderName: 'Nimesi',
    placeholderHint: 'Kirjaudu tallentaaksesi töitä, seurataksesi hakemuksia ja pitääksesi ansioluettelosi JobCrawlsissa.',
    login: 'Kirjaudu',
    installApp: 'Asenna sovellus',
  },
  hero: {
    eyebrow: 'Vakavaan työnhakuun Suomessa',
    h1Before: 'Suomalaiset sivustot + kansainvälinen, ',
    h1Em: 'yksi haku ja hakemusten seuranta',
    lead: 'Työnhaku: tuhannet yritysten urasivut ja Työmarkkinatori yhdessä, mukana myös kansainväliset työpaikat. Seuraa hakemuksesi samassa paikassa.',
    sub: 'JobCrawls kokoaa yli 17 500 avointa paikkaa, mukaan lukien etätyöt, joita muut sivustot eivät näytä. Suomeksi, ruotsiksi ja englanniksi.',
    subRich: [
      { type: 'text', text: 'JobCrawls kokoaa ' },
      { type: 'stat', id: 'activeJobs', value: '17 500+' },
      { type: 'text', text: ' avointa työpaikkaa tuhansilta ' },
      {
        type: 'link',
        label: 'yritysten urasivuilta ja Työmarkkinatorilta',
        href: '/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa',
      },
      { type: 'text', text: ' ja kansainvälisiltä etätyöalustoilta, mukana ' },
      {
        type: 'link',
        label: 'Helsingin työmarkkinatiedot',
        href: '/fi/tyoelama/helsingin-tyomarkkinat-2026',
      },
      { type: 'text', text: ' ja ' },
      {
        type: 'link',
        label: 'vuoden 2026 palkkaläpinäkyvyyslaki',
        href: '/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026',
      },
      { type: 'text', text: '. Selaa ' },
      { type: 'link', label: 'kaupunkikohtaisia solmuja', href: '/fi/sijainnit' },
      { type: 'text', text: ' tai ' },
      { type: 'link', label: 'Suomen työmarkkinaoppaita', href: '/fi/tyoelama' },
      { type: 'text', text: '.' },
    ],
    searchKeywordPlaceholder: 'Toimiala, taito tai avainsana…',
    searchLocationPlaceholder: 'Kaupunki tai etätyö',
    searchBtn: 'Hae töitä',
    tags: [
      { label: 'Etätyö', href: '/fi/tyopaikat?remoteness=remote' },
      { label: 'Englanninkieliset', href: '/fi/tyopaikat?requiredLanguages=english' },
      { label: 'Ohjelmistokehitys', href: '/fi/tyopaikat?field=software+engineering' },
      { label: 'Helsinki', href: '/fi/tyopaikat/alue/Helsinki_Suomi' },
      { label: 'Kokoaikatyö', href: '/fi/tyopaikat?jobType=full-time' },
      { label: 'Senioripaikat', href: '/fi/tyopaikat?normalisedRoleLevel=senior' },
      { label: 'Joustava työaika', href: '/fi/tyopaikat?benefits=flexible+working+hours' },
      { label: 'Työnhakusivustot', href: '/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa' },
    ],
    trackCta: 'Seuraa hakemuksiasi ilmaiseksi →',
  },
  seoPitch: {
    regionAriaLabel: 'Työnhaku, hakemusten seuranta ja lähteet',
    moreLabel: 'Lisätietoa JobCrawlsista: kenelle palvelu on ja mistä data tulee',
    points: [
      {
        title: 'Kaikki sivustot yhdellä haulla',
        desc: 'Tuhannet yritysten urasivut, Työmarkkinatori ja kansainväliset lähteet näkyvät samassa haussa: avoimet työpaikat, joita muuten metsästäisit kymmeniltä sivustoilta, yhdessä näkymässä. Työnhaku ilman rekisteröitymistä: ei välilehtiä, ei erillistä kirjautumista pelkkään selaamiseen.',
      },
      {
        title: 'Hakemusten seuranta',
        desc: 'Työnhakemuksen seuranta onnistuu samassa työnhakutyökalussa: merkitse mihin olet hakenut, mikä on hakemuksen tila ja milloin sinulle vastataan. Yksi paikka koko työnhaulle.',
      },
      {
        title: 'Kansainväliset työpaikat suomalaisille',
        desc: 'Etsitkö työtä myös Suomen rajojen ulkopuolelta? Näemme myös eurooppalaiset etätyöpaikat suomalaisten ilmoitusten rinnalla.',
      },
    ],
    audience:
      'JobCrawls on tehty työnhakijoille, jotka hakevat tosissaan ja tekevät vakavan työnhaun. Jos kierrät päivittäin useita työnhakusivustoja, seuraat hakemuksiasi taulukkolaskennassa ja haluat työnhaun organisointiin yhden näkymän sekä kansainväliset mahdollisuudet, tämä on sinulle. Ei työnantajille, ei rekrytoijille.',
    sources:
      'Indeksoimme yritysten urasivut suoraan ja kokoamme ilmoituksia myös Työmarkkinatorilta, Indeedistä sekä kansainvälisistä lähteistä. Päivitetään päivittäin.',
    trustRich: [
      { type: 'text', text: 'JobCrawlsin operoi TechTactics Oy. Lue ' },
      { type: 'link', label: 'tietoa meistä', href: '/fi/tietoa-jobcrawlsista' },
      { type: 'text', text: ', ' },
      { type: 'link', label: 'toimitusperiaatteet', href: '/fi/toimitusperiaatteet' },
      { type: 'text', text: ' tai ' },
      { type: 'link', label: 'ota yhteyttä', href: '/fi/yhteystiedot' },
      { type: 'text', text: ' ilmoituksiin tai dataan liittyen.' },
    ],
  },
  stats: [
    { num: '17 500+', label: 'Avointa työpaikkaa', id: 'activeJobs' },
    { num: '3', label: 'Kieltä tuettu' },
    { num: '1 000+', label: 'Yritystä indeksoitu' },
    { num: 'Ilmainen', label: 'Aina, ei piilokuluja' },
  ],
  introCards: [
    { iconType: 'search', title: 'Työpaikat muualta', desc: 'Indeksoimme satoja lähteitä (yritysten urapalveluita, rekrytointitoimistoja, etätyöpaikkoja), ei pelkästään maksettuja ilmoituksia.' },
    { iconType: 'filter', title: 'Syväsuodatus', desc: 'Suodata palkan, kielen, etujen, senioriteettitason ja yli 50 muun attribuutin mukaan.' },
    { iconType: 'check', title: 'Seuraa koko hakemuspolkuasi', desc: 'Tallenna ilmoitukset, aseta hakuhälytyksiä ja seuraa hakemuksia ensinäytöstä tarjoukseen.' },
  ],
  features: [
    {
      badge: 'Haku & löydettävyys',
      title: 'Suomen tehokkain työnhaku',
      desc: 'Jokainen ilmoitus sisältää rakenteisen datan: palkkaluokat, vaaditut kielet, edut, työjärjestelyt ja paljon muuta. Hae niin kuin ajattelet.',
      points: [
        'Suodata yli 50 attribuutilla',
        'Järjestä palkan, osuvuuden tai tuoreuden mukaan',
        'Etätyöt ympäri maailmaa Suomessa asuville',
        'Tallenna hakuja ja aseta sähköpostihälytyksiä',
      ],
      visualType: 'job-list',
      mockupUrl: 'jobcrawls.com/fi/tyopaikat',
      reversed: false,
    },
    {
      badge: 'Hakemuspolku',
      title: 'Hallinnoi koko työnhakuasi yhdessä paikassa',
      desc: 'Seuraa jokaista työpaikkaa ensikatselusta lopulliseen vastaukseen. Suppilosi, aikajanasi, muistiinpanosi. Synkronoituna kaikille laitteille kirjautuessa.',
      points: [
        'Kanban-tyyppinen hakemusseuranta',
        'Sankey-suppilonäkymä hakuputkestasi',
        'Katsottujen, tallennettujen ja haettujen historia',
        'Synkronoituu kaikille laitteille',
      ],
      visualType: 'kanban',
      mockupUrl: 'Hakemustoimintasi',
      reversed: true,
      ctaText: 'Katso hakemusputkesi →',
      ctaHref: '/fi/tyokalut/tyovirta-sankey',
    },
    {
      badge: 'Yritystieto',
      title: 'Tutustu yritykseen ennen hakemista',
      desc: 'Jokaisella työnantajalla on julkisesta datasta rakennettu profiili: rekrytointitrendit, työnantajapisteytys, talousluvut ja työskentelyjärjestelyt.',
      points: [
        'JobCrawls Employer Score (palkka, joustavuus, vakaus, kasvu)',
        'Liikevaihto ja kannattavuus suomalaisesta yritysrekisteristä',
        'Rekrytointitrendisignaalit: vakaa, kasvava, laskeva',
        'Parhaat työnantajat Suomessa -listaus',
      ],
      visualType: 'employer-score',
      mockupUrl: 'jobcrawls.com/fi/company/…',
      reversed: false,
      ctaText: 'Selaa yrityslistauksia →',
      ctaHref: '/fi/parhaat-yritykset',
    },
  ],
  langStrip: {
    label: 'Saatavilla kielillä',
    pills: [
      { flag: '🇫🇮', name: 'Suomi', countValue: '17 500+', countUnit: 'työpaikkaa', href: '/fi' },
      { flag: '🇸🇪', name: 'Svenska', countValue: '17 500+', countUnit: 'jobb', href: '/sv' },
      { flag: '🇬🇧', name: 'English', countValue: '17 500+', countUnit: 'jobs', href: '/en' },
    ],
    ctaText: 'Kaikki ilmoitukset käännetty koneellisesti kolmelle kielelle →',
    ctaHref: '/fi/tyopaikat',
  },
  howItWorks: {
    sectionLabel: 'Miten toimii',
    title: 'Hausta tarjoukseen yhdessä paikassa',
    steps: [
      { num: '1', title: 'Hae kaikkialta', desc: 'Kirjoita toimiala, taito tai kaupunki. Haemme sadoista lähteistä, myös sellaisista, joita maksulliset työnhakusivustot eivät listaa.' },
      { num: '2', title: 'Suodata tarkasti', desc: 'Rajaa palkan, kielen, etujen, senioriteettitason ja työjärjestelyn mukaan yli 50 attribuutilla.' },
      { num: '3', title: 'Tallenna & vertaa', desc: 'Kirjanmerkki rooleja, vertaa yritysprofiileja ja aseta hälytyksiä uusille osumille.' },
      { num: '4', title: 'Seuraa edistymistäsi', desc: 'Kirjaa hakemukset, seuraa haastatteluvaiheita ja visualisoi suppilosi, synkronoituna tiliisi.' },
    ],
  },
  resources: {
    sectionLabel: 'Resurssit & tiedot',
    title: 'Suomen työmarkkinaoppaat',
    introRich: [
      { type: 'text', text: 'Dataan perustuvia oppaita palkoista, rekrytointitrendeistä ja työnhausta Suomessa, ' },
      {
        type: 'link',
        label: 'suomalaisten työnhakusivustojen vertailusta',
        href: '/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa',
      },
      { type: 'text', text: ' ' },
      {
        type: 'link',
        label: 'Helsingin työmarkkinatietoihin',
        href: '/fi/tyoelama/helsingin-tyomarkkinat-2026',
      },
      { type: 'text', text: ' ja ' },
      {
        type: 'link',
        label: 'vuoden 2026 palkkaläpinäkyvyyslakiin',
        href: '/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026',
      },
      { type: 'text', text: '. Katso ' },
      { type: 'link', label: 'kaikki oppaat', href: '/fi/tyoelama' },
      { type: 'text', text: ' tai aloita suositellusta artikkelista.' },
    ],
    seeAllText: 'Kaikki artikkelit →',
    seeAllHref: '/fi/tyoelama',
    readMoreText: 'Lue lisää →',
    cards: [
      {
        featured: true,
        href: '/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa',
        imgSrc: 'https://www.jobcrawls.com/resources/job-websites-finland.webp',
        imgAlt: 'Parhaat työnhakusivustot Suomessa',
        date: '28. marraskuuta 2025',
        title: 'Parhaat työnhakusivustot Suomessa',
        desc: 'Löydä parhaat suomalaiset työnhakusivustot seuraavan roolisi etsimiseen.',
      },
      {
        href: '/fi/tyoelama/helsingin-tyomarkkinat-2026',
        imgSrc: 'https://www.jobcrawls.com/resources/helsinki-job-market.webp',
        imgAlt: 'Helsingin työmarkkinat 2026',
        date: '27. maaliskuuta 2026',
        title: 'Helsingin työmarkkinat 2026: Palkat, etätyö & taidot',
        desc: 'Palkkatrendit, etä- ja hybridityö sekä halutuimmat taidot Suomen pääkaupungissa.',
      },
      {
        href: '/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026',
        imgSrc: 'https://www.jobcrawls.com/resources/salary-transparency-in-finland.webp',
        imgAlt: 'Suomen palkkaläpinäkyvyyslaki 2026',
        date: '31. maaliskuuta 2026',
        title: 'Suomen palkkaläpinäkyvyyslaki 2026',
        desc: '18.5.2026 alkaen työnantajien on ilmoitettava palkkahaitari ennen neuvotteluja.',
      },
      {
        href: '/fi/tyoelama/tyot-suomessa/teknologia-palkkaopas-suomi-2026-04',
        imgSrc: 'https://www.jobcrawls.com/resources/tech-salaries-in-finland.webp',
        imgAlt: 'Teknologiapalkat Suomessa huhtikuu 2026',
        date: '2. huhtikuuta 2026',
        title: 'Teknologiapalkat Suomessa, huhtikuu 2026',
        desc: 'Kuukausipalkat senioriteettitasoittain, suosituimmat roolit ja lähityö vs. etätyö. Perustuu 1 831 ilmoitukseen.',
      },
      {
        href: '/fi/tyoelama/tyot-suomessa/suomen-piilotyomarkkinat-verkostoituminen-2026-03',
        imgSrc: 'https://www.jobcrawls.com/resources/who-you-know-matters-in-finland.webp',
        imgAlt: 'Suomen piilotyömarkkinat',
        date: '29. maaliskuuta 2026',
        title: 'Suomen piilotyömarkkinat & verkostoituminen',
        desc: '21 % suomalaisista työpaikoista ei koskaan päädy Työmarkkinatorille. Miksi verkostoituminen on tärkeää.',
      },
      {
        href: '/fi/tyoelama/etatyo-suomessa',
        imgSrc: 'https://www.jobcrawls.com/resources/remote-work-in-finland.webp',
        imgAlt: 'Etätyö Suomessa',
        date: '27. maaliskuuta 2026',
        title: 'Etätyö Suomessa (2026)',
        desc: 'Kuinka yleistä kokonaan etänä tehtävä työ on? Alan ja senioriteettitason jakaumat 16 700+ suomalaisesta ilmoituksesta.',
      },
    ],
  },
  locations: {
    sectionLabel: 'Työpaikkasijainnit',
    title: 'Jokainen suomalainen kaupunki yhdessä haussa',
    desc: 'Reaaliaikaiset työmäärät jokaiselle suomalaiselle kaupungille, alueelle ja etätyökategorialle, päivitetty päivittäin. Tutustu palkkoihin, suosittuihin työnantajiin ja halutuimpiin taitoihin sijainneittain.',
    cards: [
      { name: 'Helsinki', count: '5 200+', href: '/fi/tyopaikat/alue/Helsinki_Suomi', emoji: '🏙️', weight: 100 },
      { name: 'Etätyö', count: '3 600+', href: '/fi/tyopaikat?remoteness=remote', emoji: '🌍', weight: 69 },
      { name: 'Tampere', count: '1 500+', href: '/fi/tyopaikat/alue/Tampere_Suomi', emoji: '🏭', weight: 29 },
      { name: 'Espoo', count: '900+', href: '/fi/tyopaikat/alue/Espoo_Suomi', emoji: '🏢', weight: 17 },
      { name: 'Turku', count: '850+', href: '/fi/tyopaikat/alue/Turku_Suomi', emoji: '⚓', weight: 16 },
      { name: 'Oulu', count: '700+', href: '/fi/tyopaikat/alue/Oulu_Suomi', emoji: '❄️', weight: 13 },
      { name: 'Vantaa', count: '620+', href: '/fi/tyopaikat/alue/Vantaa_Suomi', emoji: '✈️', weight: 12 },
      { name: 'Jyväskylä', count: '400+', href: '/fi/tyopaikat/alue/Jyväskylä_Suomi', emoji: '🌲', weight: 8 },
      { name: 'Lahti', count: '360+', href: '/fi/tyopaikat/alue/Lahti_Suomi', emoji: '⛷️', weight: 7 },
      { name: 'Kuopio', count: '280+', href: '/fi/tyopaikat/alue/Kuopio_Suomi', emoji: '🎣', weight: 6 },
      { name: 'Pori', count: '220+', href: '/fi/tyopaikat/alue/Pori_Suomi', emoji: '🎷', weight: 5 },
      { name: 'Vaasa', count: '200+', href: '/fi/tyopaikat/alue/Vaasa_Suomi', emoji: '⚡', weight: 4 },
      { name: 'Lappeenranta', count: '170+', href: '/fi/tyopaikat/alue/Lappeenranta_Suomi', emoji: '🚤', weight: 3 },
      { name: 'Kotka', count: '140+', href: '/fi/tyopaikat/alue/Kotka_Suomi', emoji: '🚢', weight: 2 },
    ],
    viewAllText: 'Kaikki sijainnit →',
    viewAllHref: '/fi/sijainnit',
  },
  freeBanner: {
    title: 'Täysin ilmainen.\nEi piiloja.',
    body: 'Työnhakijan ei pitäisi maksaa työnhausta. JobCrawls on ilmainen: ei premium-tasoa, ei lukittuja suodattimia, ei piilokustannuksia.',
    primaryCta: { text: 'Selaa kaikkia töitä →', href: '/fi/tyopaikat' },
    secondaryCta: { text: 'Parhaat työnantajat Suomessa', href: '/fi/parhaat-yritykset' },
  },
  faq: {
    sectionLabel: 'UKK',
    title: 'Usein kysytyt kysymykset',
    items: [
      {
        q: 'Mikä erottaa JobCrawlsin Duunitorista tai Joblystä?',
        a: 'Duunitori ja Jobly näyttävät vain ilmoituksia, joista työnantajat maksavat. JobCrawls indeksoi satoja lähteitä (yritysten urasivuja, erikoistuneita työnhakusivustoja, kansainvälisiä etätyöalustoja), joten näet tuhansia töitä, joita näillä sivustoilla ei ole. Poimimme myös paljon enemmän rakenteista dataa jokaisesta ilmoituksesta, mikä mahdollistaa syvemmän suodatuksen.',
      },
      {
        q: 'Miksi JobCrawlsissa on töitä, joita ei löydy Duunitorista?',
        a: 'Koska kyseessä olevat yritykset eivät ole toimittaneet ilmoituksiaan Duunitoriin. Monet työnantajat julkaisevat avoimen paikan vain omalla urapalvelusivullaan tai erikoistuneilla alustoilla. Indeksoimme kaikki nämä suoraan.',
      },
      {
        q: 'Miten suodatus eroaa Duunitoriin tai Joblyyn verrattuna?',
        a: 'Duunitori ja Jobly tarjoavat perushakua avainsanoilla ja sijainnilla. JobCrawls poimii jokaisesta ilmoituksesta yli 50 rakennetta (palkkaluokat, vaadittavat kielet, edut, työskentelyjärjestelyt, senioriteetti, taidot ja paljon muuta), joten voit suodattaa juuri niin kuin oikeasti ajattelet työpaikasta.',
      },
      {
        q: 'Olen ulkomaalainen tai kansainvälinen työnhakija Suomessa. Onko JobCrawls parempi?',
        a: 'Kyllä. Sivusto toimii täysin englanniksi (sekä suomeksi ja ruotsiksi), ja nostamme esiin englanninkieliset roolit sekä globaalit etätyöpaikat Suomesta käsin, joita Duunitori ja Jobly pitkälti sivuuttavat.',
      },
      {
        q: 'Onko Suomessa asuville oikeasti etätöitä?',
        a: 'Kyllä, niitä on tuhansia. Monet kansainväliset yritykset palkkaavat täysin etänä Euroopassa tai maailmanlaajuisesti. JobCrawls nostaa nämä roolit esiin ja tekee niistä haettavia paikallisten suomalaisten töiden rinnalla. Duunitori ja Jobly indeksoivat niitä harvoin.',
      },
      {
        q: 'Onko Duunitorissa tai Joblyssä hakemusseuranta?',
        a: 'Kummallakaan ei ole sisäänrakennettua hakemustenhallintaa. JobCrawls tarjoaa Kanban-taulun jokaisen roolin seuraamiseen ensinäkemisestä tarjoukseen sekä Sankey-suppilon, joka visualisoi koko hakuputkesi yhdellä silmäyksellä.',
      },
      {
        q: 'Onko JobCrawls todella ilmainen?',
        a: 'Täysin ilmainen työnhakijoille: ei tasoja, ei lukittuja suodattimia, ei krediittejä. Koska mikään työnantaja ei maksa listautumisesta, hakutulokset järjestetään relevanssin ja tuoreuden mukaan, ei markkinointibudjetin.',
      },
      {
        q: 'Kuinka tuoreita ilmoitukset ovat verrattuna Duunitoriin?',
        a: 'Indeksoimme lähteet jatkuvasti, useimmat ilmoitukset päivitetään 24 tunnin sisällä. Voit myös suodattaa tuoreuden mukaan (viimeiset 6 tuntia, 24 tuntia, 3 päivää tai viikko) nähdäksesi vain tuoreimmat avoimet paikat.',
      },
    ],
  },
  footer: {
    tagline:
      'Yksi paikka: hae useilta suomalaisilta sivustoilta ja seuraa hakemuksia. Käyttöliittymä suomeksi, ruotsiksi ja englanniksi. Aina ilmainen työnhakijoille.',
    cols: [
      {
        title: 'Haku',
        links: [
          { label: 'Avoimet työpaikat', href: '/fi/tyopaikat' },
          { label: 'Töitä Helsingissä', href: '/fi/tyopaikat/alue/Helsinki_Suomi' },
          { label: 'Töitä Tampereella', href: '/fi/tyopaikat/alue/Tampere_Suomi' },
          { label: 'Töitä Espoossa', href: '/fi/tyopaikat/alue/Espoo_Suomi' },
          { label: 'Töitä Turussa', href: '/fi/tyopaikat/alue/Turku_Suomi' },
          { label: 'Töitä Oulussa', href: '/fi/tyopaikat/alue/Oulu_Suomi' },
          { label: 'Kaikki sijainnit →', href: '/fi/sijainnit' },
        ],
      },
      {
        title: 'Työkalut',
        links: [
          { label: 'Miten löydät töitä Suomessa', href: '/fi/tyonhaku/miten-loydat-toita-suomessa' },
          { label: 'Työhakemusten seuranta', href: '/fi/tyonhaku/tyonhakemuksen-seuranta' },
          { label: 'Ura juniorille', href: '/fi/juniors' },
          { label: 'Työnantajapiste-algoritmi', href: '/fi/about-employer-score' },
          { label: 'Hakemusseuranta', href: '/fi/tyokalut/tyovirta-sankey' },
          { label: 'Parhaat työnantajat', href: '/fi/parhaat-yritykset' },
          { label: 'Palkkavertailu', href: '/fi/tyoelama/korkeimmat-palkat-suomessa-alueittain' },
          { label: 'Kirjaudu', href: '/auth/login' },
        ],
      },
      {
        title: 'Yritys',
        links: [
          { label: 'Tietoja JobCrawlsista', href: '/fi/tietoa-jobcrawlsista' },
          { label: 'Blogi & resurssit', href: '/fi/tyoelama' },
          { label: 'Yhteystiedot', href: '/fi/yhteystiedot' },
          { label: 'Toimitusperiaatteet', href: '/fi/toimitusperiaatteet' },
          { label: 'Mediakumppanuudet', href: '/fi/kumppanuudet' },
          { label: 'Tietosuojaseloste', href: '/fi/tietosuoja' },
          { label: 'Käyttöehdot', href: '/en/terms' },
        ],
      },
    ],
    copy: '© 2026 TechTactics Oy. Kaikki oikeudet pidätetään.',
  },
};

const SV_COPY: LandingCopy = {
  locale: 'sv',
  jobsHref: '/sv/lediga-jobb',
  resourcesHref: '/sv/arbetsliv',
  nav: {
    features: 'Funktioner',
    howItWorks: 'Så fungerar det',
    resources: 'Arbetsliv',
    faq: 'FAQ',
    searchCta: 'Sök jobb →',
    signInText: 'Logga in',
    langHrefs: { en: '/en', fi: '/fi', sv: '/sv' },
  },
  userMenu: {
    menu: 'Meny',
    close: 'Stäng',
    findJobs: 'Hitta jobb',
    language: 'Språk',
    theme: 'Tema',
    themeLight: 'Ljust',
    themeDark: 'Mörkt',
    themeAuto: 'Auto',
    activity: 'Din aktivitet',
    savedJobs: 'Sparade jobb',
    viewedJobs: 'Visade jobb',
    appliedJobs: 'Ansökta jobb',
    savedSearches: 'Sparade sökningar',
    resources: 'Resurser & Blogg',
    locations: 'Platshubben',
    profile: 'Min profil',
    session: 'Session',
    signOut: 'Logga ut',
    signIn: 'Logga in',
    getStarted: 'Kom igång',
    placeholderName: 'Ditt namn',
    placeholderHint: 'Logga in för att spara jobb, spåra ansökningar och hålla ditt CV på JobCrawls.',
    login: 'Logga in',
    installApp: 'Installera app',
  },
  hero: {
    eyebrow: 'För seriösa jobbsökare i Finland',
    h1Before: 'Finska sajter och internationella listor, ',
    h1Em: 'sök och spåra tillsammans',
    lead: 'Sök bland tusentals företags karriärsidor, Työmarkkinatori och internationella källor på ett ställe. Följ dina ansökningar i samma verktyg.',
    sub: 'JobCrawls aggregerar 17 500+ tjänster, inklusive distansjobb för finländare som ingen annan sajt visar. På finska, svenska och engelska.',
    subRich: [
      { type: 'text', text: 'JobCrawls aggregerar ' },
      { type: 'stat', id: 'activeJobs', value: '17 500+' },
      { type: 'text', text: ' lediga jobb från tusentals ' },
      {
        type: 'link',
        label: 'företags karriärsidor och Työmarkkinatori',
        href: '/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland',
      },
      { type: 'text', text: ' och internationella distansplattformar, med full ' },
      {
        type: 'link',
        label: 'Helsingfors arbetsmarknadsdata',
        href: '/sv/arbetsliv/helsingfors-arbetsmarknad-2026',
      },
      { type: 'text', text: ' och ' },
      {
        type: 'link',
        label: '2026 års lönetransparenslag',
        href: '/sv/arbetsliv/lonetransparens-lag-finland-2026',
      },
      { type: 'text', text: '. Bläddra i ' },
      { type: 'link', label: 'stad-för-stad-nav', href: '/sv/platser' },
      { type: 'text', text: ' eller våra ' },
      { type: 'link', label: 'Finland arbetsmarknadsguider', href: '/sv/arbetsliv' },
      { type: 'text', text: '.' },
    ],
    searchKeywordPlaceholder: 'Jobbtitel, kompetens eller nyckelord…',
    searchLocationPlaceholder: 'Stad eller distans',
    searchBtn: 'Sök jobb',
    tags: [
      { label: 'Distans', href: '/sv/lediga-jobb?remoteness=remote' },
      { label: 'Engelskspråkiga', href: '/sv/lediga-jobb?requiredLanguages=english' },
      { label: 'Mjukvaruutveckling', href: '/sv/lediga-jobb?field=software+engineering' },
      { label: 'Helsingfors', href: '/sv/lediga-jobb/omrade/Helsingfors_Finland' },
      { label: 'Heltid', href: '/sv/lediga-jobb?jobType=full-time' },
      { label: 'Seniorroller', href: '/sv/lediga-jobb?normalisedRoleLevel=senior' },
      { label: 'Flexibla arbetstider', href: '/sv/lediga-jobb?benefits=flexible+working+hours' },
      { label: 'Jobbsajter', href: '/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland' },
    ],
    trackCta: 'Följ dina ansökningar gratis →',
  },
  seoPitch: {
    regionAriaLabel: 'Jobbsökning, ansökningar och källor',
    moreLabel: 'Mer om JobCrawls: för vem tjänsten är och varifrån vår data kommer',
    points: [
      {
        title: 'Varje ledig roll i en sökning',
        desc: 'Tusentals företags karriärsidor, Työmarkkinatori och internationella källor samlade på ett ställe: annonserna du annars skulle jaga på dussintals sajter, i ett flöde. Ingen registrering krävs.',
      },
      {
        title: 'Följ dina ansökningar',
        desc: 'Logga var du sökt, vilket steg du är på och när du ska följa upp. Ansökningsspårning i samma vy: följa ansökningar utan extra kalkylblad.',
      },
      {
        title: 'Internationella jobb vid sidan av finska',
        desc: 'Begränsar du inte sökningen till Finland? Europeiska distansjobb visas bredvid finska annonser. Praktiskt när du vill kombinera lokala och internationella jobb från Finland.',
      },
    ],
    audience:
      'JobCrawls är byggt för seriösa jobbsökare i jobbsökning Finland. Om du besöker flera jobbsajter dagligen, följer ansökningar i ett kalkylblad och vill se internationella jobb vid sidan av finska roller, är det här för dig. Inte för rekryterare. Inte för arbetsgivare.',
    sources:
      'Vi crawlar företags karriärsidor direkt och aggregerar även Työmarkkinatori, Indeed och internationella källor. Uppdateras dagligen.',
    trustRich: [
      { type: 'text', text: 'JobCrawls drivs av TechTactics Oy. Läs vår ' },
      { type: 'link', label: 'om-sida', href: '/sv/om-jobcrawls' },
      { type: 'text', text: ', ' },
      { type: 'link', label: 'redaktionella riktlinjer', href: '/sv/redaktionella-riktlinjer' },
      { type: 'text', text: ' eller ' },
      { type: 'link', label: 'kontakta oss', href: '/sv/kontakt' },
      { type: 'text', text: ' om annonser eller data.' },
    ],
  },
  stats: [
    { num: '17 500+', label: 'Lediga tjänster', id: 'activeJobs' },
    { num: '3', label: 'Språk stöds' },
    { num: '1 000-tals', label: 'Företag indexerade' },
    { num: 'Gratis', label: 'Alltid, inga betalväggar' },
  ],
  introCards: [
    { iconType: 'search', title: 'Jobb andra missar', desc: 'Vi crawlar hundratals källor (företagens karriärsidor, byråer, distansbrädor), inte bara betalda annonser.' },
    { iconType: 'filter', title: 'Djup sökning & filter', desc: 'Filtrera efter lön, språk, förmåner, senioritetsnivå och 50+ extraherade attribut per annons.' },
    { iconType: 'check', title: 'Spåra hela din jobbresa', desc: 'Spara jobb, ställ in sökalarm och spåra ansökningar från första visning till erbjudande.' },
  ],
  features: [
    {
      badge: 'Sökning & Upptäckt',
      title: 'Den mest kraftfulla jobbsökningen i Finland',
      desc: 'Varje annons innehåller strukturerad data: löneintervall, språkkrav, förmåner, arbetsarrangemang och mer. Sök på det sätt du faktiskt tänker.',
      points: [
        'Filtrera med 50+ extraherade attribut',
        'Sortera efter lön, relevans eller aktualitet',
        'Globala distansjobb tillgängliga för finländare',
        'Spara sökningar och få e-postaviseringar',
      ],
      visualType: 'job-list',
      mockupUrl: 'jobcrawls.com/sv/lediga-jobb',
      reversed: false,
    },
    {
      badge: 'Jobbresa',
      title: 'Hantera hela din jobbsökning på ett ställe',
      desc: 'Spåra varje jobb från första klick till erbjudande. Din tratt, din tidslinje, dina anteckningar. Synkroniserade mellan enheter.',
      points: [
        'Kanban-ansökningsspårning',
        'Sankey-trattvy av din pipeline',
        'Historik över visade, sparade och ansökta jobb',
        'Synkroniseras på alla dina enheter',
      ],
      visualType: 'kanban',
      mockupUrl: 'Din aktivitet',
      reversed: true,
      ctaText: 'Visa din ansökningstratt →',
      ctaHref: '/sv/verktyg/jobbflode-sankey',
    },
    {
      badge: 'Företagsinformation',
      title: 'Lär känna företaget innan du ansöker',
      desc: 'Varje arbetsgivare har en profil byggd på offentlig data: rekryteringstrender, arbetsgivarpoäng, finansiella siffror och arbetsarrangemang.',
      points: [
        'JobCrawls Employer Score (lön, flexibilitet, stabilitet, tillväxt)',
        'Omsättning och lönsamhet från det finska företagsregistret',
        'Rekryteringstrendssignaler: stabil, växande, minskande',
        'Ranking av bästa arbetsgivare i Finland',
      ],
      visualType: 'employer-score',
      mockupUrl: 'jobcrawls.com/sv/company/…',
      reversed: false,
      ctaText: 'Utforska företagshubben →',
      ctaHref: '/sv/basta-foretag',
    },
  ],
  langStrip: {
    label: 'Tillgänglig på',
    pills: [
      { flag: '🇫🇮', name: 'Suomi', countValue: '17 500+', countUnit: 'työpaikkaa', href: '/fi' },
      { flag: '🇸🇪', name: 'Svenska', countValue: '17 500+', countUnit: 'jobb', href: '/sv' },
      { flag: '🇬🇧', name: 'English', countValue: '17 500+', countUnit: 'jobs', href: '/en' },
    ],
    ctaText: 'Alla annonser maskinöversatta på alla tre språk →',
    ctaHref: '/sv/lediga-jobb',
  },
  howItWorks: {
    sectionLabel: 'Så fungerar det',
    title: 'Från sökning till erbjudande på ett ställe',
    steps: [
      { num: '1', title: 'Sök allt', desc: 'Skriv en roll, kompetens eller stad. Vi söker över hundratals källor, inklusive jobb som ingen betald jobbsajt listar.' },
      { num: '2', title: 'Filtrera exakt', desc: 'Begränsa efter lön, språk, förmåner, senioritet och arbetsarrangemang från 50+ extraherade attribut.' },
      { num: '3', title: 'Spara & jämför', desc: 'Bokmärk roller, jämför företagsprofiler och ställ in aviseringar för nya matchningar.' },
      { num: '4', title: 'Spåra dina framsteg', desc: 'Logga ansökningar, spåra intervjufaser och visualisera din pipeline, synkroniserad med ditt konto.' },
    ],
  },
  resources: {
    sectionLabel: 'Resurser & insikter',
    title: 'Guider för Finlands arbetsmarknad',
    introRich: [
      { type: 'text', text: 'Datadrivna guider om löner, rekryteringstrender och jobbsökning i Finland, från ' },
      {
        type: 'link',
        label: 'en jämförelse av finska jobbsajter',
        href: '/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland',
      },
      { type: 'text', text: ' till ' },
      {
        type: 'link',
        label: 'Helsingfors arbetsmarknadsdata',
        href: '/sv/arbetsliv/helsingfors-arbetsmarknad-2026',
      },
      { type: 'text', text: ' och ' },
      {
        type: 'link',
        label: '2026 års lönetransparenslag',
        href: '/sv/arbetsliv/lonetransparens-lag-finland-2026',
      },
      { type: 'text', text: '. Se ' },
      { type: 'link', label: 'alla guider', href: '/sv/arbetsliv' },
      { type: 'text', text: ' eller börja med en utvald artikel nedan.' },
    ],
    seeAllText: 'Se alla artiklar →',
    seeAllHref: '/sv/arbetsliv',
    readMoreText: 'Läs mer →',
    cards: [
      {
        featured: true,
        href: '/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland',
        imgSrc: 'https://www.jobcrawls.com/resources/job-websites-finland.webp',
        imgAlt: 'Bästa jobbsajter Finland',
        date: '28 november 2025',
        title: 'Bästa jobbsajter i Finland',
        desc: 'De bästa jobbsökningssajterna i Finland för att hitta din nästa roll.',
      },
      {
        href: '/sv/arbetsliv/helsingfors-arbetsmarknad-2026',
        imgSrc: 'https://www.jobcrawls.com/resources/helsinki-job-market.webp',
        imgAlt: 'Helsingfors arbetsmarknad 2026',
        date: '27 mars 2026',
        title: 'Helsingfors arbetsmarknad 2026: Löner, distans & kompetenser',
        desc: 'Lönetrender, distans- och hybridroller och mest efterfrågade kompetenser i Finlands huvudstad.',
      },
      {
        href: '/sv/arbetsliv/lonetransparens-lag-finland-2026',
        imgSrc: 'https://www.jobcrawls.com/resources/salary-transparency-in-finland.webp',
        imgAlt: 'Finlands lönetransparenslagstiftning 2026',
        date: '31 mars 2026',
        title: 'Finlands lönetransparenslag 2026',
        desc: 'Från 18 maj 2026 måste arbetsgivare dela löneintervall innan förhandlingar.',
      },
      {
        href: '/sv/arbetsliv/jobb-i-finland/teknologi-loeneguide-finland-2026-04',
        imgSrc: 'https://www.jobcrawls.com/resources/tech-salaries-in-finland.webp',
        imgAlt: 'Teknikjobb löner Finland april 2026',
        date: '2 april 2026',
        title: 'Tekniklöneguide: Finland, april 2026',
        desc: 'Månadslöner efter senioritetsnivå, topprroller från hantverkare till mjukvaruingenjörer. Baserat på 1 831 annonser.',
      },
      {
        href: '/sv/arbetsliv/jobb-i-finland/finlands-dolda-arbetsmarknad-2026-03',
        imgSrc: 'https://www.jobcrawls.com/resources/who-you-know-matters-in-finland.webp',
        imgAlt: 'Dold arbetsmarknad Finland',
        date: '29 mars 2026',
        title: 'Finlands dolda arbetsmarknad & nätverk',
        desc: '21% av finska jobb når aldrig Työmarkkinatori. Varför nätverkande spelar roll.',
      },
      {
        href: '/sv/arbetsliv/distansarbete-i-finland',
        imgSrc: 'https://www.jobcrawls.com/resources/remote-work-in-finland.webp',
        imgAlt: 'Distansarbete Finland',
        date: '27 mars 2026',
        title: 'Distansarbete i Finland (2026)',
        desc: 'Hur vanligt är helt distansarbete? Bransch- och senioritetsuppdelning från 16 700+ finska annonser.',
      },
    ],
  },
  locations: {
    sectionLabel: 'Jobbplatser',
    title: 'Varje stad i Finland, i en sökning',
    desc: 'Jobbsiffror för varje finsk stad, region och distanskategori, uppdateras dagligen. Utforska löner, topparbetsgivare och efterfrågade kompetenser per plats.',
    cards: [
      { name: 'Helsingfors', count: '5 200+', href: '/sv/lediga-jobb/omrade/Helsingfors_Finland', emoji: '🏙️', weight: 100 },
      { name: 'Distans', count: '3 600+', href: '/sv/lediga-jobb?remoteness=remote', emoji: '🌍', weight: 69 },
      { name: 'Tammerfors', count: '1 500+', href: '/sv/lediga-jobb/omrade/Tammerfors_Finland', emoji: '🏭', weight: 29 },
      { name: 'Esbo', count: '900+', href: '/sv/lediga-jobb/omrade/Esbo_Finland', emoji: '🏢', weight: 17 },
      { name: 'Åbo', count: '850+', href: '/sv/lediga-jobb/omrade/Åbo_Finland', emoji: '⚓', weight: 16 },
      { name: 'Uleåborg', count: '700+', href: '/sv/lediga-jobb/omrade/Uleåborg_Finland', emoji: '❄️', weight: 13 },
      { name: 'Vanda', count: '620+', href: '/sv/lediga-jobb/omrade/Vanda_Finland', emoji: '✈️', weight: 12 },
      { name: 'Jyväskylä', count: '400+', href: '/sv/lediga-jobb/omrade/Jyväskylä_Finland', emoji: '🌲', weight: 8 },
      { name: 'Lahtis', count: '360+', href: '/sv/lediga-jobb/omrade/Lahtis_Finland', emoji: '⛷️', weight: 7 },
      { name: 'Kuopio', count: '280+', href: '/sv/lediga-jobb/omrade/Kuopio_Finland', emoji: '🎣', weight: 6 },
      { name: 'Björneborg', count: '220+', href: '/sv/lediga-jobb/omrade/Björneborg_Finland', emoji: '🎷', weight: 5 },
      { name: 'Vasa', count: '200+', href: '/sv/lediga-jobb/omrade/Vasa_Finland', emoji: '⚡', weight: 4 },
      { name: 'Villmanstrand', count: '170+', href: '/sv/lediga-jobb/omrade/Villmanstrand_Finland', emoji: '🚤', weight: 3 },
      { name: 'Kotka', count: '140+', href: '/sv/lediga-jobb/omrade/Kotka_Finland', emoji: '🚢', weight: 2 },
    ],
    viewAllText: 'Alla platser →',
    viewAllHref: '/sv/platser',
  },
  freeBanner: {
    title: 'Helt gratis.\nInga kryphål.',
    body: 'Ingen jobbsökare ska behöva betala för att hitta ett jobb. JobCrawls är gratis: ingen premiumplan, inga låsta filter, inga dolda betalväggar.',
    primaryCta: { text: 'Bläddra bland alla jobb →', href: '/sv/lediga-jobb' },
    secondaryCta: { text: 'Bästa arbetsgivare i Finland', href: '/sv/basta-foretag' },
  },
  faq: {
    sectionLabel: 'FAQ',
    title: 'Vanliga frågor',
    items: [
      {
        q: 'Vad skiljer JobCrawls från Duunitori eller Jobly?',
        a: 'Duunitori och Jobly visar bara annonser som arbetsgivare betalar för att publicera där. JobCrawls crawlar hundratals källor (företagens karriärsidor, nischade brädor, internationella distansplattformar), så du ser tusentals jobb som aldrig dyker upp på de sajterna. Vi extraherar också mycket rikare strukturerad data per annons, vilket möjliggör filtrering som Duunitori och Jobly helt enkelt inte erbjuder.',
      },
      {
        q: 'Varför finns det jobb på JobCrawls som inte finns på Duunitori?',
        a: 'För att de företagen aldrig skickade in sina lediga tjänster till Duunitori. Många arbetsgivare publicerar bara på sin egen karriärsida eller på specialiserade plattformar. Vi crawlar alla dessa direkt, så skillnaden kan vara tusentals roller vid varje given tidpunkt.',
      },
      {
        q: 'Hur skiljer sig filtreringen från Duunitori eller Jobly?',
        a: 'Duunitori och Jobly erbjuder grundläggande nyckelords- och platssökning. JobCrawls extraherar 50+ strukturerade attribut från varje annons (löneintervall, nödvändiga språk, förmåner, arbetsarrangemang, senioritetsnivå, kompetenser med mera), så du kan filtrera på det sätt du faktiskt tänker på ett jobb.',
      },
      {
        q: 'Jag är utländsk eller internationell jobbsökare i Finland. Är JobCrawls bättre för mig?',
        a: 'Ja. Sajten fungerar fullt ut på engelska (samt finska och svenska) och vi lyfter fram engelskspråkiga roller och globala distansjobb tillgängliga från Finland, kategorier som Duunitori och Jobly i stort sett ignorerar.',
      },
      {
        q: 'Finns det verkligen distansjobb för personer bosatta i Finland?',
        a: 'Ja, tusentals. Många globala företag anställer helt på distans var som helst i Europa eller världen. JobCrawls lyfter fram dessa roller och gör dem sökbara bredvid lokala finska annonser. Duunitori och Jobly indexerar dem sällan.',
      },
      {
        q: 'Har Duunitori eller Jobly ansökningsspårning?',
        a: 'Ingen av dem erbjuder inbyggd ansökningshantering. JobCrawls ger dig en Kanban-tavla för att spåra varje roll från första titt till erbjudande, plus ett Sankey-diagram som visualiserar hela din pipeline i ett svep.',
      },
      {
        q: 'Är JobCrawls verkligen gratis?',
        a: 'Helt gratis för jobbsökare: inga nivåer, inga låsta filter, inga krediter. Och eftersom ingen arbetsgivare betalar för att listas, rankas resultat efter relevans och aktualitet, inte efter vem som har den största rekryteringsbudgeten.',
      },
      {
        q: 'Hur färska är annonserna jämfört med Duunitori?',
        a: 'Vi crawlar källor kontinuerligt, med de flesta annonser uppdaterade inom 24 timmar. Du kan också filtrera efter aktualitet (senaste 6 timmarna, 24 timmar, 3 dagar eller 1 vecka) för att fokusera på de allra färskaste öppningarna.',
      },
    ],
  },
  footer: {
    tagline:
      'Ett ställe: sök över finska jobbsajter och spåra ansökningar. Gränssnitt på finska, svenska och engelska. Alltid gratis för jobbsökare.',
    cols: [
      {
        title: 'Sök',
        links: [
          { label: 'Lediga jobb', href: '/sv/lediga-jobb' },
          { label: 'Jobb i Helsingfors', href: '/sv/lediga-jobb/omrade/Helsingfors_Finland' },
          { label: 'Jobb i Tammerfors', href: '/sv/lediga-jobb/omrade/Tammerfors_Finland' },
          { label: 'Jobb i Esbo', href: '/sv/lediga-jobb/omrade/Esbo_Finland' },
          { label: 'Jobb i Åbo', href: '/sv/lediga-jobb/omrade/Åbo_Finland' },
          { label: 'Jobb i Uleåborg', href: '/sv/lediga-jobb/omrade/Uleåborg_Finland' },
          { label: 'Alla platser →', href: '/sv/platser' },
        ],
      },
      {
        title: 'Verktyg',
        links: [
          { label: 'Hur hittar man jobb i Finland', href: '/sv/jobbsok/hur-hittar-man-jobb-i-finland' },
          { label: 'Jobbansökningsspårare', href: '/sv/jobbsok/jobbansokningssparare' },
          { label: 'Karriär för juniorer', href: '/sv/juniors' },
          { label: 'Arbetsgivarpoäng-algoritmen', href: '/sv/about-employer-score' },
          { label: 'Ansökningsspårning', href: '/sv/verktyg/jobbflode-sankey' },
          { label: 'Bästa arbetsgivare', href: '/sv/basta-foretag' },
          { label: 'Lönejämförelse', href: '/sv/arbetsliv/hogsta-lonerna-finland-regioner' },
          { label: 'Logga in', href: '/auth/login' },
        ],
      },
      {
        title: 'Företag',
        links: [
          { label: 'Om JobCrawls', href: '/sv/om-jobcrawls' },
          { label: 'Blogg & resurser', href: '/sv/arbetsliv' },
          { label: 'Kontakt', href: '/sv/kontakt' },
          { label: 'Redaktionella riktlinjer', href: '/sv/redaktionella-riktlinjer' },
          { label: 'Mediepartnerskap', href: '/sv/partnerskap' },
          { label: 'Integritetspolicy', href: '/sv/integritet' },
          { label: 'Användarvillkor', href: '/en/terms' },
        ],
      },
    ],
    copy: '© 2026 TechTactics Oy. Alla rättigheter förbehållna.',
  },
};

const COPY_BY_LOCALE: Record<LandingLocale, LandingCopy> = {
  en: EN_COPY,
  fi: FI_COPY,
  sv: SV_COPY,
};

export function getLandingCopy(locale: LandingLocale): LandingCopy {
  return COPY_BY_LOCALE[locale] ?? COPY_BY_LOCALE.en;
}
